/**
 * Booking Buffer utilities.
 *
 * Enforce a minimum gap between consecutive bookings.
 * Useful for cleaning time, travel time, or setup time.
 *
 * Buffer config in listing.publicData.bookingBuffer = {
 *   beforeMinutes: 30,  — gap before booking start
 *   afterMinutes: 60,   — gap after booking end (cleaning time)
 * }
 *
 * Both values are in minutes.
 */

export const DEFAULT_BUFFER_BEFORE = 0;
export const DEFAULT_BUFFER_AFTER = 0;
export const MAX_BUFFER_MINUTES = 1440; // 24 hours

/**
 * Get booking buffer config from listing.
 * @param {Object|null} listing
 * @returns {{ beforeMinutes: number, afterMinutes: number }}
 */
export const getBookingBuffer = (listing) => {
  const buf = listing?.attributes?.publicData?.bookingBuffer;
  return {
    beforeMinutes: buf?.beforeMinutes ?? DEFAULT_BUFFER_BEFORE,
    afterMinutes: buf?.afterMinutes ?? DEFAULT_BUFFER_AFTER,
  };
};

/**
 * Add buffer to booking time window.
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {{ beforeMinutes, afterMinutes }} buffer
 * @returns {{ blockedStart: Date, blockedEnd: Date }}
 */
export const applyBuffer = (startDate, endDate, buffer = {}) => {
  const { beforeMinutes = DEFAULT_BUFFER_BEFORE, afterMinutes = DEFAULT_BUFFER_AFTER } = buffer;
  const blockedStart = new Date(startDate.getTime() - beforeMinutes * 60 * 1000);
  const blockedEnd = new Date(endDate.getTime() + afterMinutes * 60 * 1000);
  return { blockedStart, blockedEnd };
};

/**
 * Check if a new booking overlaps with existing booking + buffer.
 * @param {Date} newStart
 * @param {Date} newEnd
 * @param {Date} existingStart
 * @param {Date} existingEnd
 * @param {{ beforeMinutes, afterMinutes }} buffer
 * @returns {boolean}
 */
export const hasBufferConflict = (newStart, newEnd, existingStart, existingEnd, buffer = {}) => {
  const { blockedStart, blockedEnd } = applyBuffer(existingStart, existingEnd, buffer);
  return newStart < blockedEnd && newEnd > blockedStart;
};

/**
 * Validate buffer config.
 * @param {Object} buffer
 * @returns {string|null}
 */
export const validateBuffer = (buffer) => {
  if (!buffer) return null; // buffer is optional
  if (buffer.beforeMinutes !== undefined && (buffer.beforeMinutes < 0 || buffer.beforeMinutes > MAX_BUFFER_MINUTES)) {
    return `Before buffer must be 0-${MAX_BUFFER_MINUTES} minutes`;
  }
  if (buffer.afterMinutes !== undefined && (buffer.afterMinutes < 0 || buffer.afterMinutes > MAX_BUFFER_MINUTES)) {
    return `After buffer must be 0-${MAX_BUFFER_MINUTES} minutes`;
  }
  return null;
};
